/**
 * @description Component-EarnLiquiditiesMobile
 */
import { usePairFromUrl } from '@/features/pair/hook';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';
import './index.less';

import { Button } from '@/components/Button';
import Empty from '@/components/Empty';
import { EmptyDataWrap } from '@/components/EmptyDataWrap';
import { Skeleton } from '@/components/Skeleton';
import TokenPairSimple from '@/components/TokenPair/TokenPairSimple';
import { Tooltip } from '@/components/ToolTip';
import { EARN_TYPE } from '@/constants/earn';
import { PAIR_PAGE_TYPE } from '@/constants/global';
import { TRADE_OVERLAY_TYPE } from '@/constants/trade';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { setEarnFormType } from '@/features/earn/action';
import { useEarnStore } from '@/features/earn/store';
import { useIsIpBlocked } from '@/features/global/hooks';
import { useFetchPortfolioListFromApi } from '@/features/portfolio/query';
import { useTradeStore } from '@/features/trade/store';
import { useAppDispatch } from '@/hooks';
import { useFilteredLiquidities } from '@/hooks/trade/useFilteredLiquidities';
import { useSideNavigate } from '@/hooks/useRouterNavigate';
import LiquidationPrices from '@/pages/components/LiquidationPrices';
import PriceRange from '@/pages/earn/EarnLiquidities/PriceRange';
import { TabKeys } from '@/pages/trade/TradePortfolio/TradePortfolioTabs';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
interface IPropTypes {
  children?: React.ReactNode;
  className?: string;
  isTradePage?: boolean;
}
const EarnLiquiditiesMobile: FC<IPropTypes> = function ({ isTradePage = true }) {
  const chainId = useChainId();
  const userAddr = useUserAddr();
  const ipBlocked = useIsIpBlocked();
  const currentPair = usePairFromUrl(chainId);
  const rangeList = useFilteredLiquidities(currentPair, isTradePage);
  const { setShowTradeAreaOverlay } = useTradeStore();
  const { setCurrentRangeId } = useEarnStore();

  const { isFetched: isFetchedPortfolio } = useFetchPortfolioListFromApi(chainId, userAddr);
  const { pairPageNavigate } = useSideNavigate();

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  if (!isFetchedPortfolio) {
    return (
      <div className="syn-earn-liquidities-mobile-skeleton">
        <div className="syn-earn-liquidities-mobile-skeleton-top">
          <Skeleton active={true} paragraph={false} />
          <Skeleton active={true} paragraph={false} />
        </div>
        <div className="syn-earn-liquidities-mobile-skeleton-row">
          <Skeleton active={true} paragraph={false} />
          <Skeleton active={true} paragraph={false} />
          <Skeleton active={true} paragraph={false} />
        </div>
      </div>
    );
  }

  return (
    <div className="syn-earn-liquidities-mobile">
      {rangeList?.map((range) => {
        return (
          <div
            className="syn-mobile-profile-item"
            key={range.id}
            onClick={() => {
              pairPageNavigate(range.rootPair.symbol, PAIR_PAGE_TYPE.EARN, undefined, {
                state: {
                  tab: TabKeys.LIQUIDITY,
                },
              });
            }}>
            <div className="syn-mobile-profile-item-header">
              <div className="syn-mobile-profile-item-title">
                {' '}
                <EmptyDataWrap isLoading={!range?.rootPair}>
                  <TokenPairSimple
                    // tradeSide={range.wrappedSide}
                    // leverage={range.leverage}
                    isInverse={range?.rootPair.isInverse}
                    baseToken={range?.rootPair?.rootInstrument.baseToken}
                    quoteToken={range?.rootPair?.rootInstrument.quoteToken}
                  />
                </EmptyDataWrap>
              </div>
              <div className="syn-mobile-profile-item-btns">
                <Tooltip showOnMobile={true} title={ipBlocked ? t('common.ipBlocker.tooltip') : undefined}>
                  <Button
                    ghost
                    disabled={ipBlocked}
                    onClick={(e) => {
                      e.stopPropagation();

                      if (chainId) {
                        setCurrentRangeId(range.id);
                        dispatch(setEarnFormType({ chainId, formType: EARN_TYPE.REMOVE_LIQ }));
                      }
                      pairPageNavigate(range.rootPair.symbol, PAIR_PAGE_TYPE.EARN, undefined, {
                        state: {
                          tab: TabKeys.LIQUIDITY,
                        },
                      });

                      const isCurrentPair = currentPair?.id === range.rootPair.id;
                      setTimeout(
                        () => {
                          setShowTradeAreaOverlay(TRADE_OVERLAY_TYPE.REMOVE_LIQ);
                        },
                        isCurrentPair ? 0 : 500,
                      );
                    }}
                    className="syn-trade-open-orders-cancel-btn">
                    {t('common.remove')}
                  </Button>
                </Tooltip>
              </div>
            </div>
            <div className="syn-mobile-profile-item-body">
              <PriceRange
                minPrice={range.wrapAttribute('lowerPrice')}
                maxPrice={range.wrapAttribute('upperPrice')}
                isInverse={range.rootPair.rootInstrument.isInverse}
                pairId={range.rootPair.id}
                fairPrice={range.rootPair.wrapAttribute('fairPrice')}
              />
              <dl>
                <dt>{t('common.earn.entryPrice')}</dt>
                <dd>
                  <EmptyDataWrap isLoading={!range}>
                    {WrappedBigNumber.from(range.entryPrice || 0).formatPriceNumberWithTooltip()}
                  </EmptyDataWrap>
                </dd>
              </dl>
              <dl>
                <dt>{t('common.earn.valueLocked')}</dt>
                <dd>
                  <EmptyDataWrap isLoading={!range}>
                    {range.wrapAttribute('valueLocked').formatNumberWithTooltip({
                      suffix: range.rootInstrument.marginToken.symbol,
                      showToolTip: true,
                    })}
                  </EmptyDataWrap>
                </dd>
              </dl>
              <dl>
                <dt>{t('common.earn.feesE')}</dt>
                <dd>
                  <EmptyDataWrap isLoading={!range?.feeEarned}>
                    {range.wrapAttribute('feeEarned').formatNumberWithTooltip({
                      suffix: range.rootInstrument.marginToken.symbol,
                    })}
                  </EmptyDataWrap>
                </dd>
              </dl>
              <dl>
                <dt>{t('common.liqPrice')}</dt>
                <dd>
                  <LiquidationPrices
                    lowerLiqPrice={range.wrappedLowerLiqPrice}
                    upperLiqPrice={range.wrappedUpperLiqPrice}
                  />
                </dd>
              </dl>
            </div>
          </div>
        );
      })}

      {!rangeList?.length && <Empty type="vertical" />}
    </div>
  );
};

export default EarnLiquiditiesMobile;
